import React, { useState, useEffect } from 'react';
import { X, Shield, AlertTriangle, Save } from 'lucide-react';
import { AutoApprovalRule } from '../../types';

interface AutoApprovalRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  rule?: AutoApprovalRule | null;
  onSave: (rule: Omit<AutoApprovalRule, 'id'>) => void;
}

const AutoApprovalRuleModal: React.FC<AutoApprovalRuleModalProps> = ({
  isOpen,
  onClose,
  rule,
  onSave
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'credit_limit' as 'credit_limit' | 'wallet_load' | 'registration',
    condition: '',
    maxAmount: '',
    minGrade: '',
    kycRequired: true,
    departmentWhitelist: [] as string[],
    isActive: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);

  const departments = ['Engineering', 'Marketing', 'Sales', 'HR', 'Finance', 'Operations'];
  const grades = ['Junior', 'Mid-Level', 'Senior', 'Manager', 'Director', 'Executive'];

  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name,
        type: rule.type,
        condition: rule.condition,
        maxAmount: rule.maxAmount?.toString() || '',
        minGrade: rule.minGrade || '',
        kycRequired: rule.kycRequired,
        departmentWhitelist: rule.departmentWhitelist || [],
        isActive: rule.isActive
      });
      setSelectedDepartments(rule.departmentWhitelist || []);
    } else {
      setFormData({
        name: '',
        type: 'credit_limit',
        condition: '',
        maxAmount: '',
        minGrade: '',
        kycRequired: true,
        departmentWhitelist: [],
        isActive: true
      });
      setSelectedDepartments([]);
    }
    setErrors({});
  }, [rule, isOpen]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors["name"] = 'Rule name is required';
    if (!formData.condition.trim()) newErrors["condition"] = 'Condition description is required';
    
    if (formData.type === 'credit_limit' || formData.type === 'wallet_load') {
      if (!formData.maxAmount) newErrors["maxAmount"] = 'Maximum amount is required';
      if (parseFloat(formData.maxAmount) <= 0) newErrors["maxAmount"] = 'Amount must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    const ruleData = {
      ...formData,
      maxAmount: formData.maxAmount ? parseFloat(formData.maxAmount) : 0,
      departmentWhitelist: selectedDepartments
    };

    onSave(ruleData);
    onClose();
  };

  const handleDepartmentToggle = (dept: string) => {
    setSelectedDepartments(prev => 
      prev.includes(dept) 
        ? prev.filter(d => d !== dept)
        : [...prev, dept]
    );
  };

  const handleClose = () => {
    setFormData({
      name: '',
      type: 'credit_limit',
      condition: '',
      maxAmount: '',
      minGrade: '',
      kycRequired: true,
      departmentWhitelist: [],
      isActive: true
    });
    setSelectedDepartments([]);
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  const isEditing = !!rule;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Shield className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Auto-Approval Rule' : 'Add Auto-Approval Rule'}
              </h2>
              <p className="text-sm text-gray-600">
                {isEditing ? 'Modify existing rule configuration' : 'Create a new automatic approval rule'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, name: e.target.value }));
                    if (errors["name"]) setErrors(prev => ({ ...prev, name: '' }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["name"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Senior Staff Credit Limit"
                />
                {errors["name"] && <p className="mt-1 text-sm text-red-600">{errors["name"]}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rule Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="credit_limit">Credit Limit Increase</option>
                  <option value="wallet_load">Wallet Load</option>
                  <option value="registration">Employee Registration</option>
                </select>
              </div>
            </div>
          </div>

          {/* Rule Conditions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rule Conditions</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condition Description *
                </label>
                <textarea
                  value={formData.condition}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, condition: e.target.value }));
                    if (errors["condition"]) setErrors(prev => ({ ...prev, condition: '' }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors["condition"] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Describe when this rule should apply..."
                  rows={3}
                />
                {errors["condition"] && <p className="mt-1 text-sm text-red-600">{errors["condition"]}</p>}
              </div>

              {(formData.type === 'credit_limit' || formData.type === 'wallet_load') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Amount (₦) *
                  </label>
                  <input
                    type="number"
                    value={formData.maxAmount}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, maxAmount: e.target.value }));
                      if (errors["maxAmount"]) setErrors(prev => ({ ...prev, maxAmount: '' }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors["maxAmount"] ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="50000"
                    min="0"
                    step="1000"
                  />
                  {errors["maxAmount"] && <p className="mt-1 text-sm text-red-600">{errors["maxAmount"]}</p>}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Grade Level
                  </label>
                  <select
                    value={formData.minGrade}
                    onChange={(e) => setFormData(prev => ({ ...prev, minGrade: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Any Grade</option>
                    {grades.map((grade) => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="kycRequired"
                    checked={formData.kycRequired}
                    onChange={(e) => setFormData(prev => ({ ...prev, kycRequired: e.target.checked }))}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="kycRequired" className="ml-2 text-sm text-gray-700">
                    Require KYC verification
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Department Restrictions */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Department Restrictions</h3>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                Select departments where this rule applies. Leave empty to apply to all departments.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {departments.map((dept) => (
                  <label key={dept} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={selectedDepartments.includes(dept)}
                      onChange={() => handleDepartmentToggle(dept)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">{dept}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Rule Status */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Rule Status</h3>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                Rule is active and will be applied automatically
              </label>
            </div>
          </div>

          {/* Warning for High Amounts */}
          {formData.maxAmount && parseFloat(formData.maxAmount) > 100000 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  <strong>High Amount Warning:</strong> This rule allows amounts over ₦100,000. 
                  Please ensure this is appropriate for your organization's risk tolerance.
                </span>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              {isEditing ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutoApprovalRuleModal;
